/**
 * THE RESULT LOCK (BK, 28 Jul).
 *
 * "If a user tries to leave the match chat or click on any other items but
 * submit result we need a pop up... This forces users to submit results and not
 * leave matches unresolved. The other user will always be brought back to the
 * same match to submit results before they can continue to engage."
 *
 * Two behaviours off one predicate:
 *   · IN the match — a nav tap is intercepted and answered with the prompt.
 *   · ANYWHERE ELSE — they are returned to the match they owe.
 *
 * ── THE THING THIS MUST NEVER DO ────────────────────────────────────────────
 * Trap somebody. A lock that fires on a state it misread is not a nudge, it is
 * a player who cannot use the app at all and has no idea why. So:
 *
 *   · It never fires before `/match/active` has answered. The same mistake as
 *     the onboarding gate: acting on a half-loaded state locks EVERY player on
 *     EVERY cold start, because "no match yet" and "match not fetched yet" look
 *     identical.
 *   · It fails OPEN. A trust/engine outage means `loaded` stays false and the
 *     app behaves normally. Being unable to reach the server must not also mean
 *     being unable to move.
 *   · It only ever fires on a match that genuinely owes THIS player a score —
 *     `resultOwed`, which is false for VOIDED / EXPIRED / WALKOVER / already
 *     submitted. A dead match can never hold anyone.
 *
 * The way out is always forward and always present: submitting clears it, and
 * the dispute exits live inside the match centre, so a player who cannot
 * honestly report a score still has somewhere to go.
 */

/** The prompt. BK's words. */
export const RESULT_LOCK_TITLE = "HOLD ON THERE COACH";
export const RESULT_LOCK_BODY = "Submit your result before you can keep playing.";
export const RESULT_LOCK_CTA = "SUBMIT RESULT";

/**
 * What is owed. Filing your own score is not the only obligation with a clock
 * on it — checking THEIR score is the other, and it was invisible.
 *
 * 13 Aug: THEWIZARD filed 0–1 with a screenshot at 10:53:28. SERGIOAG returned
 * to the app, was shown nothing anywhere, and the score auto-confirmed against
 * them 5 minutes later. The lock only understood "you owe a score", so the
 * instant the opponent submitted, the match left RESULT_PENDING and the lock
 * released the one player who still had something to do. Inaction is agreement
 * here — that has to be a decision, not an accident.
 */
export type LockKind = "submit" | "verify";

export const VERIFY_LOCK_BODY =
  "Your opponent's score is in. Check it before it counts.";
export const VERIFY_LOCK_CTA = "CHECK THE SCORE";

export function lockBody(kind: LockKind): string {
  return kind === "verify" ? VERIFY_LOCK_BODY : RESULT_LOCK_BODY;
}

export function lockCta(kind: LockKind): string {
  return kind === "verify" ? VERIFY_LOCK_CTA : RESULT_LOCK_CTA;
}

export interface LockInput {
  /** The match still owing a score from this player, or null when none does. */
  owedMatchId: string | null;
  /** False until GET /match/active has answered at least once. */
  loaded: boolean;
  /** The route the player is on right now. */
  path: string;
}

/** The match centre's own path for a given match. */
export function matchPath(matchId: string): string {
  return `/m/${matchId}`;
}

/**
 * Is the player currently inside the match they owe?
 *
 * Exact path or a sub-route of it — NOT a bare `startsWith`, which would count
 * `/m/abcdef` as being inside `/m/abc`. Match ids are UUIDs so that cannot
 * happen today, but "the id format saves us" is not a reason to write a
 * comparison that is wrong.
 */
export function onOwedMatch(i: LockInput): boolean {
  if (i.owedMatchId === null) return false;
  const base = matchPath(i.owedMatchId);
  return i.path === base || i.path.startsWith(`${base}/`);
}

/**
 * Should a navigation attempt be stopped and answered with the prompt?
 *
 * Only while they are ON the match. Off it, `bounceTarget` has already sent
 * them back — blocking as well would leave a player looking at a modal on a
 * screen they are about to be moved off, which reads as the app fighting itself.
 */
export function navBlocked(i: LockInput): boolean {
  if (!i.loaded) return false;
  if (i.owedMatchId === null) return false;
  return onOwedMatch(i);
}

/**
 * Where the player must be returned to, or null to leave them alone.
 *
 * This is the "always brought back to the same match" half. It returns a path
 * rather than navigating, so the decision stays testable and the caller owns
 * the one side effect.
 */
export function bounceTarget(i: LockInput): string | null {
  if (!i.loaded) return null;
  if (i.owedMatchId === null) return null;
  if (onOwedMatch(i)) return null; // already where they need to be
  return matchPath(i.owedMatchId);
}
